'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

import { installErrorHandlers } from '../../src/analytics/error-tracking';
import { installWebObservability } from '../../src/observability/install';
import { fetchAuthStatus } from '../../src/providers/auth';
import { AuthCallback } from '../../src/components/AuthCallback';
import { Welcome } from '../../src/components/Welcome';
import { HandoffLanding } from '../../src/components/handoff/HandoffLanding';
import { readHandoffCodeFromPath } from '../../src/providers/gini-handoff';

export const AUTH_LOGGED_OUT_EVENT = 'gini-studio:auth-logged-out';

// Install browser exception handlers at module-load time, before any other
// client code can throw. The hooks buffer events until AnalyticsProvider
// finishes `bootstrapExceptionTracking()` with the PostHog key, so even
// errors thrown during the dynamic import of `src/App` are captured.
installErrorHandlers();

// Install the rest of the observability surface (long tasks, white-screen
// detector, resource-error capture, boot timing, visibility tracking).
// Same buffer + consent-bypass transport as the exception handler above
// so events fired before AnalyticsProvider initialises still flush.
installWebObservability();

// The product is a fully client-driven SPA — every component reads
// localStorage, window.location, etc. — so we opt out of static-time
// rendering for the entire tree. This keeps `next build --output export`
// from trying to evaluate browser-only code while still emitting a real
// shell HTML the daemon can serve as the SPA fallback.
const App = dynamic(() => import('../../src/App').then((m) => m.App), {
  ssr: false,
  loading: () => <div className="od-loading-shell">Loading GINI Studio…</div>,
});

// Gate in front of <App/>. On local/desktop installs (no auth required)
// `/api/auth/me` always answers `{authenticated:true, mode:'local'}`, so
// this resolves to rendering <App/> immediately. Standalone native auth
// and hosted OD_AUTH_MODE=server both gate through Welcome.
type GateState =
  | { status: 'checking' }
  | { status: 'app' }
  | { status: 'welcome' }
  | { status: 'callback' }
  | { status: 'handoff'; code: string };

function isAuthCallbackPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/auth/callback';
}

// `/handoff/:code` is checked BEFORE the auth gate, not after: the landing
// page owns its own sign-in-and-resume flow, and routing an unauthenticated
// visitor through the generic Welcome gate first would drop the code (the
// generic gate does not preserve the path it interrupted).
function handoffCodeFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  return readHandoffCodeFromPath(window.location.pathname);
}

export function ClientApp() {
  const [gate, setGate] = useState<GateState>({ status: 'checking' });

  useEffect(() => {
    function onLoggedOut() {
      if (isAuthCallbackPath() || handoffCodeFromLocation()) {
        window.history.replaceState(null, '', '/');
      }
      setGate({ status: 'welcome' });
    }
    window.addEventListener(AUTH_LOGGED_OUT_EVENT, onLoggedOut);
    return () => window.removeEventListener(AUTH_LOGGED_OUT_EVENT, onLoggedOut);
  }, []);

  useEffect(() => {
    if (isAuthCallbackPath()) {
      setGate({ status: 'callback' });
      return;
    }
    const handoffCode = handoffCodeFromLocation();
    if (handoffCode) {
      setGate({ status: 'handoff', code: handoffCode });
      return;
    }
    let cancelled = false;
    void fetchAuthStatus().then((status) => {
      if (cancelled) return;
      setGate({ status: status.authenticated ? 'app' : 'welcome' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function enterApp() {
    if (isAuthCallbackPath() || handoffCodeFromLocation()) {
      // Clear the one-time code out of the URL bar on the way into the app
      // so a refresh or a shared screenshot cannot replay it.
      window.history.replaceState(null, '', '/');
    }
    setGate({ status: 'app' });
  }

  if (gate.status === 'checking') {
    return <div className="od-loading-shell">Loading GINI Studio…</div>;
  }
  if (gate.status === 'callback') {
    return <AuthCallback onSignedIn={enterApp} onFallbackToLogin={() => setGate({ status: 'welcome' })} />;
  }
  if (gate.status === 'handoff') {
    return <HandoffLanding code={gate.code} onExit={enterApp} />;
  }
  if (gate.status === 'welcome') {
    return <Welcome onSignedIn={enterApp} />;
  }
  return <App />;
}
